import React,{useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "@/auth";
import {api} from "@/lib/api";
import {toast} from "sonner";

export default function AssistantBackupPage(){
 const {user}=useAuth();
 const navigate=useNavigate();
 const [loading,setLoading]=useState(false);
 const [status,setStatus]=useState("");
 const [percentage,setPercentage]=useState(null);
 const [received,setReceived]=useState(0);
 const [totalBytes,setTotalBytes]=useState(0);
 if(user?.role!=="assistant")return <div className="fc-card p-5">Assistant role required.</div>;
 const download=async()=>{
  if(loading)return;
  setLoading(true);
  setPercentage(null);
  setReceived(0);
  setTotalBytes(0);
  setStatus("Preparing operational backup…");
  try{
   const started=await api.post("/admin/backup/start");
   const id=started.data?.job_id;
   if(!id)throw new Error("Could not start backup");
   let result=null;
   for(let i=0;i<900;i++){
    await new Promise(resolve=>setTimeout(resolve,2000));
    const r=await api.get("/admin/backup/status/"+encodeURIComponent(id));
    if(r.data?.status==="failed")throw new Error("Server could not prepare backup");
    if(r.data?.status==="ready"){result=r.data;break;}
   }
   if(!result)throw new Error("Backup preparation timed out");
   const expected=Number(result.bytes)||0;
   setTotalBytes(expected);
   setPercentage(0);
   setStatus("Downloading backup…");
   const response=await api.get("/admin/backup/file/"+encodeURIComponent(id),{
     responseType:"blob",
     onDownloadProgress:event=>{
       const loaded=Number(event.loaded)||0;
       const total=Number(event.total)||expected;
       setReceived(loaded);
       if(total>0){
         setTotalBytes(total);
         setPercentage(Math.min(99,Math.floor(loaded/total*100)));
       }
     }
   });
   if(!(response.headers?.["content-type"]||"").includes("zip"))throw new Error("Unexpected download format");
   const match=(response.headers?.["content-disposition"]||"").match(/filename="?([^";]+)"?/i);
   const filename=match?.[1]||result.filename||"FreshCare_Assistant_Operational_Backup.zip";
   const url=URL.createObjectURL(new Blob([response.data],{type:"application/zip"}));
   const a=document.createElement("a");a.href=url;a.download=filename;
   document.body.appendChild(a);a.click();a.remove();
   setTimeout(()=>URL.revokeObjectURL(url),60000);
   setPercentage(100);
   setStatus("Backup downloaded");
   toast.success("Operational backup downloaded");
  }catch(e){setStatus("Backup failed");setPercentage(null);toast.error(e.response?.data?.detail||e.message||"Backup failed");}
  finally{setLoading(false);}
 };
 return <div className="max-w-2xl mx-auto pb-16 space-y-5">
  <button onClick={()=>navigate(-1)} className="text-sm text-blue-600">← Back</button>
  <div className="fc-card p-5 space-y-4">
   <h1 className="text-xl font-semibold">One-Tap Operational Backup</h1>
   <p className="text-sm text-slate-600">Download quotations, Production Cards, chassis and vehicle data, order checklists and available business documents. Admin-only settings, authentication data and application source are not included.</p>
   <button type="button" onClick={download} disabled={loading} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">{loading?"Preparing backup…":"Download One-Tap Backup"}</button>
   {status&&<div role="status" aria-live="polite" className="space-y-2">
     <p className="text-sm text-slate-600">{status}{percentage!==null?" — "+percentage+"%":""}</p>
     {loading&&percentage===null&&<div className="h-2 overflow-hidden rounded-full bg-blue-100" aria-label="Preparing backup; percentage not available"><div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" /></div>}
     {percentage!==null&&<div className="h-2 overflow-hidden rounded-full bg-blue-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage} aria-label="Backup download progress"><div className="h-full rounded-full bg-blue-600 transition-all" style={{width:percentage+"%"}} /></div>}
     {percentage!==null&&totalBytes>0&&<p className="text-xs text-slate-500">{(Math.min(received,totalBytes)/1048576).toFixed(1)} of {(totalBytes/1048576).toFixed(1)} MB received{percentage===100?" (browser save requested)":""}</p>}
     {loading&&percentage===null&&<p className="text-xs text-slate-500">The server is generating documents and the ZIP. Its preparation time cannot yet be measured as a percentage.</p>}
   </div>}
  </div>
 </div>;
}
